<template>
  <div v-if="isVisible" class="ssh-panel" :class="{ 'fullscreen': isFullscreen, 'minimized': isMinimized }" :style="panelStyle">
    <div 
      v-if="!isFullscreen"
      class="ssh-panel-resize-handle"
      @mousedown="startResize"
    ></div>
    <div class="ssh-panel-header">
      <v-tabs
        v-model="activeTab"
        background-color="grey darken-4"
        dark
        height="40"
        hide-slider
      >
        <v-tab
          v-for="session in sessions"
          :key="session.id"
          class="ssh-tab"
        >
          <span class="tab-label">{{ session.deviceName }}</span>
          <v-btn
            icon
            x-small
            class="ml-2"
            @click.stop="closeSession(session.id)"
          >
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </v-tab>
      </v-tabs>
      <div class="ssh-panel-actions">
        <v-btn
          icon
          small
          dark
          @click="toggleFullscreen"
          class="mr-1"
        >
          <v-icon small>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          dark
          @click="toggleMinimize"
          class="mr-1"
        >
          <v-icon small>{{ isMinimized ? 'mdi-window-maximize' : 'mdi-window-minimize' }}</v-icon>
        </v-btn>
        <v-btn
          icon
          small
          dark
          @click="closeAll"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </div>
    </div>
    <div v-show="!isMinimized" class="ssh-panel-content" :style="contentStyle">
      <v-tabs-items v-model="activeTab" class="ssh-tabs-items">
        <v-tab-item
          v-for="session in sessions"
          :key="session.id"
          class="ssh-tab-item"
        >
          <SSHTerminal
            ref="terminals"
            :session="session"
            :is-active="activeSessionId === session.id"
          ></SSHTerminal>
        </v-tab-item>
      </v-tabs-items>
    </div>
  </div>
</template>

<style scoped>
.ssh-panel {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #2b2b2b;
  box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  pointer-events: auto;
}

.ssh-panel.minimized {
  height: 40px !important;
}

.ssh-panel.fullscreen {
  top: 48px;
  height: calc(100vh - 48px) !important;
  bottom: auto;
}

.ssh-panel-resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 5px;
  cursor: ns-resize;
  background: transparent;
  z-index: 1001;
}

.ssh-panel-resize-handle:hover {
  background: rgba(224, 64, 251, 0.3);
}

.ssh-panel-header {
  display: flex;
  align-items: center;
  background: #1e1e1e;
  border-bottom: 1px solid #444;
}

.ssh-panel-header .v-tabs {
  flex: 1;
}

.ssh-tab {
  text-transform: none;
  min-width: 120px;
  max-width: 200px;
}

.tab-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ssh-panel-actions {
  display: flex;
  align-items: center;
  padding: 0 8px;
  background: #1e1e1e;
}

.ssh-panel-content {
  overflow: hidden;
  flex: 1;
}

.ssh-tabs-items {
  height: 100%;
  background: #2b2b2b;
}

.ssh-tab-item {
  height: 100%;
}
</style>

<script>
import SSHTerminal from './SSHTerminal.vue'

export default {
  components: {
    SSHTerminal
  },
  data() {
    return {
      activeTab: 0,
      isMinimized: false,
      isFullscreen: false,
      panelHeight: 440,
      isResizing: false,
      startY: 0,
      startHeight: 0
    }
  },
  computed: {
    sessions() {
      return this.$store.getters.sshSessions
    },
    activeSessionId() {
      return this.$store.state.sshSessions.activeSessionId
    },
    isVisible() {
      return this.$store.getters.isSshPanelVisible
    },
    panelStyle() {
      if (this.isMinimized) {
        return { height: '40px' }
      }
      if (this.isFullscreen) {
        return { height: 'calc(100vh - 48px)' }
      }
      return { height: this.panelHeight + 'px' }
    },
    contentStyle() {
      if (this.isFullscreen) {
        return { height: 'calc(100vh - 48px - 40px)' }
      }
      return { height: (this.panelHeight - 40) + 'px' }
    }
  },
  watch: {
    activeTab(newIndex) {
      if (this.sessions[newIndex]) {
        this.$store.dispatch('SetActiveSSHSession', this.sessions[newIndex].id)
      }
    },
    activeSessionId(newId) {
      const index = this.sessions.findIndex(s => s.id === newId)
      if (index !== -1 && index !== this.activeTab) {
        this.activeTab = index
      }
    }
  },
  mounted() {
    document.addEventListener('mousemove', this.handleResize)
    document.addEventListener('mouseup', this.stopResize)
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.handleResize)
    document.removeEventListener('mouseup', this.stopResize)
  },
  methods: {
    updatePanelState() {
      this.$store.dispatch('UpdateSSHPanelState', {
        height: this.panelHeight,
        isMinimized: this.isMinimized,
        isFullscreen: this.isFullscreen
      })
    },
    closeSession(sessionId) {
      this.$store.dispatch('CloseSSHSession', sessionId)
    },
    closeAll() {
      this.$store.dispatch('CloseAllSSHSessions')
      this.isMinimized = false
      this.isFullscreen = false
    },
    toggleMinimize() {
      this.isMinimized = !this.isMinimized
      if (this.isMinimized) {
        this.isFullscreen = false
      }
      this.updatePanelState()
      this.$nextTick(() => {
        this.resizeTerminals()
      })
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
      if (this.isFullscreen) {
        this.isMinimized = false
      }
      this.updatePanelState()
      this.$nextTick(() => {
        this.resizeTerminals()
      })
    },
    resizeTerminals() {
      if (this.$refs.terminals) {
        const terminals = Array.isArray(this.$refs.terminals) ? this.$refs.terminals : [this.$refs.terminals]
        terminals.forEach(terminal => {
          if (terminal && typeof terminal.resize === 'function') {
            terminal.resize()
          }
        })
      }
    },
    startResize(event) {
      this.isResizing = true
      this.startY = event.clientY
      this.startHeight = this.panelHeight
      event.preventDefault()
    },
    handleResize(event) {
      if (!this.isResizing) return
      const deltaY = this.startY - event.clientY
      const newHeight = this.startHeight + deltaY
      const maxHeight = window.innerHeight - 48
      if (newHeight >= 200 && newHeight <= maxHeight) {
        this.panelHeight = Math.min(newHeight, maxHeight)
        this.updatePanelState()
        this.$nextTick(() => {
          this.resizeTerminals()
        })
      }
    },
    stopResize() {
      this.isResizing = false
    }
  }
}
</script>

